// 帖子相关的 Action 层，负责业务逻辑和异步操作
import { action, makeObservable, runInAction } from 'mobx';
import { TopicStore } from './store';
import { readTopicThreadList, readTopicList, readTopicsFollows, readTopicDetail, topicsFollow, topicsUnFollow, readTopicCategoryList, readTopicsByIds } from '@server/api';
import { BASE_IMG_URL } from '@images/imageUrls';
export class TopicAction extends TopicStore {
    constructor(props) {
        super(props);
        makeObservable(this);
    }

    /**
     * 应用启动时预加载所有话题数据
     * 包括所有分类和每个分类的话题
     */
    @action
    async initAllTopicsData() {
        if (this.allTopicsData) {
            // 已加载过，直接返回
            return { success: true, data: { topicList: this.allTopicsData, categoryList: this.categoryList } };
        }

        runInAction(() => {
            this.topicsLoading = true;
        });

        try {
            // 获取分类
            const categoryRes = await this.getTopicCategoryList({ 'page[number]': 1, 'page[size]': 20 });
            const categoryArr = categoryRes?.data?.data || [];
            if (!categoryArr.length) {
                runInAction(() => {
                    this.topicsLoading = false;
                });
                return { success: false, message: '无法加载分类' };
            }

            const categoryList = categoryArr.map((c) => c.name);
            const categoryMap = categoryArr.reduce((acc, c) => { acc[c.name] = c.id; return acc; }, {});

            // 获取所有分类的话题
            let allTopics = [];
            for (const category of categoryArr) {
                // 加载前 3 页，每页 100 条
                for (let page = 1; page <= 3; page++) {
                    await this.getTopicList({ 'filter[categoryId]': category.id, 'page[number]': page, 'page[size]': 100 });
                    const rawList = this.listStore.getList({ namespace: 'topicList' }) || [];
                    if (rawList.length === 0) break;

                    const categoryTopics = rawList.map((item) => ({
                        id: String(item.id),
                        name: item.name,
                        cover: item.avatar || `${BASE_IMG_URL}/common/official2.png`,
                        count: `${item.postCount || 0}帖子 ${item.discussionCount || 0}讨论量`,
                        category: category.name,
                    }));
                    allTopics = [...allTopics, ...categoryTopics];
                }
            }

            // 去重
            const topicMap = new Map();
            allTopics.forEach(t => {
                if (!topicMap.has(t.id)) {
                    topicMap.set(t.id, t);
                }
            });
            allTopics = Array.from(topicMap.values());

            // 保存到 store
            runInAction(() => {
                this.allTopicsData = allTopics;
                this.categoryList = categoryList;
                this.categoryMap = categoryMap;
                this.topicsLoading = false;
            });

            console.log('[TopicStore] initAllTopicsData completed:', allTopics.length, 'topics,', categoryList.length, 'categories');
            return { success: true, data: { topicList: allTopics, categoryList } };
        } catch (error) {
            console.error('[TopicStore] initAllTopicsData error:', error);
            runInAction(() => {
                this.topicsLoading = false;
            });
            return { success: false, message: error.message };
        }
    }

    /**
     * 获取话题分类列表（异步）
     */
    async getTopicCategoryList(params = {}) {
        const res = await readTopicCategoryList(params);
        const success = res?.code === 0 || res?.Code === 0;
        if (success) return res;
        return res;
    }

    /** 获取关注的话题列表（异步） */
    getTopicsFollows(params) {
        return readTopicsFollows(params)
            .then(res => {

                if (!res) {
                    return [[], [], []];
                }

                if (res.code !== 0) {
                    return [[], [], []];
                }

                if (!res.data || !res.data.data) {
                    return [[], [], []];
                }

                // 分割数据
                const splitResult = this.splitArray(
                    res.data?.data
                      .filter(item => item.isPinned === true)  // 过滤出 isPinned 为 true 的元素
                      .slice(0, 5)  // 取前5个
                  );

                // 保存到 store（如果需要）
                // this.setFollowTopicList(splitResult);

                return splitResult;
            })
            .catch(error => {
                // 获取关注话题失败:', error
                return [[], [], []];
            });
    }

    @action
    setFollowTopicList(followTopicList) {
        this.followTopicList = followTopicList;
    }
    @action
    setFollowTopicFocusId(followTopicFocusId) {
        this.followTopicFocusId = followTopicFocusId;
    }
    @action
    getFollowTopicFocusId() {
        return this.followTopicFocusId;
    }
    
    // 分割导航
    splitArray(dataArray) {
        // 参数验证
        if (!Array.isArray(dataArray)) {
            return [[], [], []];
        }
        let cutNum = 0;
        // 无论数组长度如何，都分割
        if (dataArray.length <= 3) {
            cutNum = 3;
        } else {
            cutNum = 4;
        }
        const firstArray = dataArray.slice(0, cutNum);  // 前3个元素
        const secondArray = dataArray.slice(cutNum);    // 从第4个开始的所有剩余元素
        const allArray = [...dataArray];           // 全部元素的副本
        return [firstArray, secondArray, allArray];
    }

    /** 获取话题详情 */
    async getTopicDetail(id, params) {
        const res = await readTopicDetail(id, params);
        this.setTopicDetail(res.data);
        return res
    }

    @action
    setTopicDetail(data) {
        this.topicDetail = data;
    }

    /**
     * 通用方法：更新话题详情的关注状态
     * 支持多种数据结构：
     * 1. 简单对象 {id: 1, isFollowed: false}
     * 2. 对象包含 data: {id: 1, isFollowed: false}
     * 3. 数组 [{id: 1, isFollowed: false}, {id: 2, isFollowed: true}]
     * 4. 对象包含 data 数组 {data: [{id: 1, isFollowed: false}]}
     */
    @action
    updateTopicFollowStatus(id, isFollowing, target = 'topicDetail') {
        const targetData = this[target];

        if (!targetData) {
            // 数据不存在，无法更新
            return false;
        }

        // 更新 ${target} 中话题 ${id} 关注状态: ${isFollowing}

        // 递归查找并更新
        const updateRecursive = (obj, path = '') => {
            if (Array.isArray(obj)) {
                // 处理数组
                let updated = false;
                obj.forEach((item, index) => {
                    if (item && String(item.id) === String(id)) {
                        item.isFollowed = isFollowing;
                        // 更新 ${path}[${index}] 成功
                        updated = true;
                    }

                    // 递归处理嵌套对象
                    Object.keys(item || {}).forEach(key => {
                        if (item[key] && typeof item[key] === 'object') {
                            updateRecursive(item[key], `${path}[${index}].${key}`);
                        }
                    });
                });
                return updated;
            } else if (obj && typeof obj === 'object') {
                // 处理对象
                if (String(obj.id) === String(id)) {
                    obj.isFollowed = isFollowing;
                    // 更新 ${path} 成功
                    return true;
                }

                // 递归处理嵌套对象
                let updated = false;
                Object.keys(obj).forEach(key => {
                    if (obj[key] && typeof obj[key] === 'object') {
                        if (updateRecursive(obj[key], `${path}.${key}`)) {
                            updated = true;
                        }
                    }
                });
                return updated;
            }
            return false;
        };

        const updated = updateRecursive(targetData, target);

        if (!updated) {
            //在 ${target} 中未找到话题 ${id}`
        }

        return updated;
    }

    /** 关注话题 */
    async createTopicsFollow(id, params) {
        try {
            const res = await topicsFollow(id, params);

            if (res?.code === 0) {
                this.updateTopicFollowStatus(id, true);

                // 可选：同时更新其他相关数据
                // this.updateFollowTopicListStatus(id, true);

                //关注话题 ${id} 成功
                return { success: true, data: res.data };
            } else {
                //关注失败:', res?.msg
                return { success: false, error: res?.msg };
            }
        } catch (error) {
            //关注异常:', error
            return { success: false, error: error.message };
        }
    }

    /** 取消关注话题 */
    async createTopicsUnFollow(id, params) {
        try {
            const res = await topicsUnFollow(id);

            if (res?.code === 0) {
                this.updateTopicFollowStatus(id, false);

                // 可选：同时更新其他相关数据
                // this.updateFollowTopicListStatus(id, false);

                // 取消关注话题 ${id} 成功`
                return { success: true, data: res.data };
            } else {
                // 取消关注失败:', res?.msg
                return { success: false, error: res?.msg };
            }
        } catch (error) {
            // 取消关注异常:', error
            return { success: false, error: error.message };
        }
    }

    /**
     * 更新关注话题列表中的状态
     */
    // @action
    // updateFollowTopicListStatus(id, isFollowing) {
    //     // if (!this.followTopicList) return;

    //     // 处理 followTopicList 的更新
    //     this.updateTopicFollowStatus(id, isFollowing, 'followTopicList');
    // }

    /**
     * 根据话题ID批量获取话题详情
     * @param {Object} params - 参数对象，包含 filter.id (话题ID字符串，逗号分隔)
     * @returns {Promise<Object>} 返回包含话题列表的响应
     */
    @action
    async readTopicsByIds(params = {}) {
        try {
            const res = await readTopicsByIds(params);
            const success = res?.code === 0 || res?.Code === 0;
            if (success) {
                return res;
            }
            return res;
        } catch (error) {
            console.error('[TopicStore] readTopicsByIds error:', error);
            return { code: -1, message: error.message };
        }
    }

    /**
     * 获取话题列表（异步）
     * 统一管理 loading、错误、分页、hasMore 等状态
     */
    @action
    async getTopicList(params = {}) {
        runInAction(() => {
            this.listStore.setAttribute({ namespace: 'topicList', key: 'loading', value: true });
        });
        const res = await readTopicList(params);
        const success = res?.code === 0 || res?.Code === 0;
        const resData = res?.data || res?.Data;
        runInAction(() => {
            if (success) {
                const list = resData?.data || [];
                const page = resData?.page || {};
                this.listStore.setList({
                    namespace: 'topicList',
                    data: { data: list },
                    page: page.number || params['page[number]'],
                    path: 'data',
                    search: { filter: params.filter || {} },
                    hasMore: !!page.hasMore,
                });
                this.listStore.setAttribute({ namespace: 'topicList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'topicList', key: 'requestError', value: { isError: true, errorText: res?.message || res?.Message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'topicList', key: 'loading', value: false });
        });
        return res;
    }

    @action
    setTopicList(topicList) {
        this.topicList = topicList;
    }

    /** 获取帖子列表（异步） */
    @action
    async getTopicThreadList(params) {
        // 同一列表只接受最后一次请求的结果，避免切换 tab 后旧请求回写旧数据。
        this.topicThreadListRequestId = (this.topicThreadListRequestId || 0) + 1;
        const requestId = this.topicThreadListRequestId;
        // 先标记 loading
        runInAction(() => {
            this.listStore.setAttribute({ namespace: 'topicThreadList', key: 'loading', value: true });
        });
        const res = await readTopicThreadList(params);
        const success = res?.code === 0 || res?.Code === 0;
        const resData = res?.data || res?.Data;
        // 在 runInAction 中进行所有 observable 更新，避免严格模式报错
        runInAction(() => {
            if (requestId !== this.topicThreadListRequestId) {
                return;
            }
            if (success) {
                this.listStore.setList({
                    namespace: 'topicThreadList',
                    data: { data: resData },
                    page: params['page[number]'],
                    path: 'data.data',
                    search: { filter: params.filter || {} },
                    hasMore: resData?.page ? !!resData.page.hasMore : false,
                });
                this.listStore.setAttribute({ namespace: 'topicThreadList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'topicThreadList', key: 'requestError', value: { isError: true, errorText: res?.message || res?.Message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'topicThreadList', key: 'loading', value: false });
        });
        return res;
    }

    /** 直接设置帖子列表（通常不推荐，建议用 listStore） */
    @action
    setTopicThreadList(topicThreadList) {
        this.topicThreadList = topicThreadList;
    }

    /**
     * 更新 topicThreadList 中某条帖子的点赞状态
     * 供点赞请求成功后由调用方触发，使主列表与置顶卡片区数据保持一致
     * @param {object} params
     * @param {number|string} params.id 帖子 id
     * @param {boolean} params.isLiked 是否点赞
     * @param {number} [params.likeCount] 最新点赞数（可选）
     */
    @action
    updateThreadInTopicList({ id, isLiked, likeCount }) {
        if (id == null) return false;
        const updates = { isLiked: !!isLiked };
        if (typeof likeCount === 'number') {
            updates.likeCount = likeCount;
        }
        return this.listStore.updateListItem({
            namespace: 'topicThreadList',
            id,
            updates,
        });
    }
}

// 单例导出，兼容现有默认导入
const topicAction = new TopicAction();
export default topicAction;
